// ===========================================
// ACCOUNT TIMELINE — notes you wrote + things that happened, one stream
// ===========================================
// Human notes stay as cards (yours, editable, deletable). Derived events —
// orders placed, invoices paid, deductions taken, the standing order, stage
// moves — weave in chronologically as muted one-liners. A CRM timeline is
// usually what someone remembered to type; this one includes what the
// records say happened whether anyone typed it or not.

import { useState } from 'react';
import clsx from 'clsx';
import { CalendarClock, Trash2, Package, Receipt, Scissors, Repeat, Flag, ChevronRight, ChevronDown } from 'lucide-react';
import { fmtDayYear, timeAgo } from '../../utils/cpg';
import { isOverdue, type AutoEvent } from './insights';

type Entry =
  | { type: 'note'; at: string; note: any }
  | { type: 'auto'; at: string; event: AutoEvent };

const EVENT_ICON: Record<AutoEvent['icon'], typeof Package> = {
  order: Package, invoice: Receipt, deduction: Scissors, standing: Repeat, stage: Flag,
};

const SHOW_FIRST = 25;

export default function Timeline({ activities, autoEvents, onDeleteNote, deletePending }: {
  activities: any[];
  autoEvents: AutoEvent[];
  onDeleteNote: (activityId: string) => void;
  deletePending: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const entries: Entry[] = [
    ...activities.map((n: any): Entry => ({ type: 'note', at: n.at, note: n })),
    ...autoEvents.map((e): Entry => ({ type: 'auto', at: e.at, event: e })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  if (entries.length === 0) {
    return (
      <p className="text-sm text-slate-400">
        Nothing yet — notes you save stay on the customer record, and orders, invoices
        and deductions will appear here on their own as they happen.
      </p>
    );
  }

  const shown = expanded ? entries : entries.slice(0, SHOW_FIRST);

  return (
    <div className="space-y-2">
      {shown.map((entry) => {
        if (entry.type === 'note') {
          const a = entry.note;
          return (
            <div key={`note-${a.id}`} className="rounded-lg bg-slate-50 p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-slate-400">{fmtDayYear(a.at)} · {timeAgo(a.at)}</p>
                <button onClick={() => onDeleteNote(a.id)} disabled={deletePending}
                  className="p-0.5 text-slate-300 hover:text-red-500" title="Remove note">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{a.note}</p>
              {a.nextStep && (
                <p className={clsx('text-xs mt-1.5 flex items-center gap-1', isOverdue(a.dueAt) ? 'text-red-600 font-medium' : 'text-slate-500')}>
                  <CalendarClock className="w-3 h-3 shrink-0" />
                  Next: {a.nextStep}{a.dueAt ? ` — due ${fmtDayYear(a.dueAt)}` : ''}{isOverdue(a.dueAt) ? ' · overdue' : ''}
                </p>
              )}
            </div>
          );
        }
        const e = entry.event;
        const Icon = EVENT_ICON[e.icon];
        return (
          <div key={`auto-${e.id}`} className="flex items-start gap-2 px-3 py-1">
            <Icon className="w-3.5 h-3.5 text-slate-300 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-slate-500 truncate">{e.text}</p>
              {e.sub && <p className="text-[11px] text-slate-400 truncate">{e.sub}</p>}
            </div>
            <span className="text-[11px] text-slate-300 shrink-0" title={fmtDayYear(e.at)}>{timeAgo(e.at)}</span>
          </div>
        );
      })}

      {entries.length > SHOW_FIRST && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary-600 font-medium flex items-center gap-1">
          {expanded
            ? <><ChevronDown className="w-3.5 h-3.5" /> Show recent only</>
            : <><ChevronRight className="w-3.5 h-3.5" /> Show all {entries.length} entries</>}
        </button>
      )}

      <p className="text-[11px] text-slate-400">
        Cards are your notes; the muted lines are what happened in Mise — orders, invoices, deductions, standing orders.
      </p>
    </div>
  );
}
